const STORAGE_KEY = "cc.course_interest";
const MAX_RECENT = 5;

export type CourseInterestSource =
  | "course_view"
  | "advisor_cta"
  | "online_course_view"
  | "callback_form"
  | string;

export type CourseInterest = {
  event_detail_id: string;
  title: string;
  url?: string;
  path?: string;
  source: CourseInterestSource;
  captured_at: string;
  online_slug?: string;
};

export type LeadCourse = {
  id: string;
  title: string;
  event_detail_id?: string;
  url?: string;
  price?: string | number;
  online_slug?: string;
};

type CourseInterestStore = {
  last?: CourseInterest;
  recent: CourseInterest[];
};

type PageCourseInput = {
  event?: { id?: string | null } | null;
  entry?: {
    sys?: { id?: string };
    fields?: { title?: string; url?: string; type?: string };
  } | null;
  path?: string;
  source?: CourseInterestSource;
};

function canUseStorage() {
  return typeof window !== "undefined";
}

function readStore(): CourseInterestStore {
  if (!canUseStorage()) {
    return { recent: [] };
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { recent: [] };
    }

    const parsed = JSON.parse(raw) as CourseInterestStore;
    return {
      last: parsed?.last,
      recent: Array.isArray(parsed?.recent) ? parsed.recent : [],
    };
  } catch {
    return { recent: [] };
  }
}

function writeStore(store: CourseInterestStore) {
  if (!canUseStorage()) {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore quota / private mode failures.
  }
}

function normalizeInterest(
  interest: Partial<CourseInterest> | null | undefined,
): CourseInterest | null {
  if (!interest) {
    return null;
  }

  const eventDetailId = String(interest.event_detail_id || "").trim();
  const title = String(interest.title || "").trim();

  if (!eventDetailId || !title) {
    return null;
  }

  return {
    event_detail_id: eventDetailId,
    title,
    url: interest.url ? String(interest.url) : undefined,
    path: interest.path ? String(interest.path) : undefined,
    source: interest.source || "course_view",
    captured_at: interest.captured_at || new Date().toISOString(),
    online_slug: interest.online_slug
      ? String(interest.online_slug)
      : undefined,
  };
}

export function courseInterestFromPageCourse(
  input: PageCourseInput,
): CourseInterest | null {
  const eventDetailId = String(
    input.event?.id || input.entry?.sys?.id || "",
  ).trim();
  const title = String(input.entry?.fields?.title || "").trim();
  const url = input.entry?.fields?.url
    ? String(input.entry.fields.url)
    : undefined;

  if (!eventDetailId || !title) {
    return null;
  }

  return normalizeInterest({
    event_detail_id: eventDetailId,
    title,
    url,
    path:
      input.path ||
      (canUseStorage() ? window.location.pathname : url) ||
      undefined,
    source: input.source || "course_view",
    captured_at: new Date().toISOString(),
  });
}

export function courseInterestFromOnlineCourse(input: {
  slug: string;
  title: string;
  url?: string;
  path?: string;
}): CourseInterest | null {
  const slug = String(input.slug || "").trim();
  const title = String(input.title || "").trim();

  if (!slug || !title) {
    return null;
  }

  return normalizeInterest({
    event_detail_id: `online:${slug}`,
    title,
    url: input.url || `/courses/elearning/${slug}/`,
    path:
      input.path ||
      (canUseStorage() ? window.location.pathname : undefined) ||
      `/courses/elearning/${slug}/`,
    source: "online_course_view",
    online_slug: slug,
    captured_at: new Date().toISOString(),
  });
}

export function recordCourseInterest(
  interest: CourseInterest | null | undefined,
) {
  const next = normalizeInterest(interest);
  if (!next) {
    return;
  }

  const store = readStore();
  const recent = [
    next,
    ...store.recent.filter(
      (item) => item.event_detail_id !== next.event_detail_id,
    ),
  ].slice(0, MAX_RECENT);

  writeStore({
    last: next,
    recent,
  });
}

export function getLastCourseInterest(): CourseInterest | null {
  return normalizeInterest(readStore().last);
}

export function getRecentCourseInterest(): CourseInterest[] {
  return readStore()
    .recent.map((item) => normalizeInterest(item))
    .filter(Boolean) as CourseInterest[];
}

export function interestToLeadCourse(interest: CourseInterest): LeadCourse {
  return {
    id: interest.event_detail_id,
    event_detail_id: interest.event_detail_id,
    title: interest.title,
    url: interest.url || interest.path,
    ...(interest.online_slug ? { online_slug: interest.online_slug } : {}),
  };
}

export function buildAdvisorCallbackPath(
  interest?: CourseInterest | null,
  source = "course_page",
) {
  const params = new URLSearchParams();
  params.set("source", source);

  if (interest?.event_detail_id) {
    params.set("event_detail_id", interest.event_detail_id);
  }

  if (interest?.title) {
    params.set("course_title", interest.title);
  }

  if (interest?.url || interest?.path) {
    params.set("course_url", interest.url || interest.path || "");
  }

  if (interest?.online_slug) {
    params.set("online_slug", interest.online_slug);
  }

  return `/request-a-callback/?${params.toString()}`;
}

export function courseInterestFromSearchParams(
  search: string | URLSearchParams | null | undefined,
): CourseInterest | null {
  if (!search) {
    return null;
  }

  const params =
    typeof search === "string"
      ? new URLSearchParams(
          search.startsWith("?") ? search.slice(1) : search,
        )
      : search;

  const eventDetailId = params.get("event_detail_id") || "";
  const title = params.get("course_title") || "";
  const url = params.get("course_url") || undefined;
  const onlineSlug = params.get("online_slug") || undefined;
  const source = params.get("source") || "course_page";

  return normalizeInterest({
    event_detail_id: eventDetailId,
    title,
    url,
    path: url,
    source,
    online_slug: onlineSlug,
    captured_at: new Date().toISOString(),
  });
}

export function resolveLeadCourses({
  basketCourses = [],
  pageCourse = null,
  search = null,
  includeStoredInterest = true,
}: {
  basketCourses?: LeadCourse[];
  pageCourse?: CourseInterest | LeadCourse | null;
  search?: string | URLSearchParams | null;
  includeStoredInterest?: boolean;
} = {}): {
  courses: LeadCourse[];
  interest: CourseInterest | null;
  recent: CourseInterest[];
} {
  const fromSearch = courseInterestFromSearchParams(search);
  const fromStorage = includeStoredInterest ? getLastCourseInterest() : null;
  const recent = getRecentCourseInterest();

  const pageAsInterest =
    pageCourse && "event_detail_id" in pageCourse && pageCourse.event_detail_id
      ? normalizeInterest(pageCourse as CourseInterest)
      : pageCourse && "id" in pageCourse && pageCourse.id
        ? normalizeInterest({
            event_detail_id: String(pageCourse.id),
            title: pageCourse.title,
            url: pageCourse.url,
            source: "callback_form",
            captured_at: new Date().toISOString(),
            online_slug: (pageCourse as LeadCourse).online_slug,
          })
        : null;

  const interest = fromSearch || pageAsInterest || fromStorage;

  if (basketCourses.length > 0) {
    return {
      courses: basketCourses.map((course) => ({
        ...course,
        id: course.id,
        event_detail_id: course.event_detail_id || course.id,
        title: course.title,
      })),
      interest: interest || null,
      recent,
    };
  }

  if (interest) {
    return {
      courses: [interestToLeadCourse(interest)],
      interest,
      recent,
    };
  }

  return {
    courses: [],
    interest: null,
    recent,
  };
}

export function buildCourseInterestPreferences(
  interest: CourseInterest | null,
  recent: CourseInterest[] = [],
) {
  if (!interest && recent.length === 0) {
    return {};
  }

  return {
    ...(interest?.path || interest?.url
      ? { last_course_page: interest.path || interest.url }
      : {}),
    ...(interest?.event_detail_id
      ? { last_event_detail_id: interest.event_detail_id }
      : {}),
    ...(interest?.title ? { last_course_title: interest.title } : {}),
    recent_courses: (recent.length > 0 ? recent : interest ? [interest] : [])
      .slice(0, MAX_RECENT)
      .map((item) => ({
        event_detail_id: item.event_detail_id,
        title: item.title,
        url: item.url || item.path,
        source: item.source,
        captured_at: item.captured_at,
        ...(item.online_slug ? { online_slug: item.online_slug } : {}),
      })),
  };
}
